'use client'

import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import type clsx from 'clsx'
import type { ReactNode } from 'react'

import { useTopNavV6 } from '@src/components/TopNavV6/TopNavV6Context'
import type { TopNavV6Props } from '@src/components/TopNavV6/TopNavV6Props'
import { useBreakpoints } from '@src/utils/breakpoints.util'
import { cn } from '@src/utils/tailwind'

import { TopNavV6MobileCloseHeader } from './TopNavV6MobileCloseHeader'
import { TopNavV6MobileMenu } from './TopNavV6MobileMenu'
import { TopNavV6MobileMiniMenu } from './TopNavV6MobileMiniMenu'
import { TopNavV6MobileThirdLevelMenu } from './TopNavV6MobileThirdLevelMenu'

const asideClassNames =
  'fixed inset-y-0 left-0 z-[100] w-full text-white flex flex-col duration-300 transition-all ease-in-out'

const backgroundGradient =
  'linear-gradient(-34.46788108107425deg, #ffffff14, #fff0 75.951%), linear-gradient(90deg, #161819 0%, #161819 100%)'

const Panel = ({
  open,
  onClose,
  className,
  content,
}: {
  open: boolean
  onClose: () => void
  className?: clsx.ClassValue
  content: ReactNode
}) => {
  return (
    <Dialog
      transition
      className="relative z-[100] transition duration-300 ease-in-out"
      onClose={onClose}
      open={open}
      static
    >
      <Transition show={open} as="div" className="fixed inset-0">
        {/* Backdrop */}
        <TransitionChild
          as="div"
          className="fixed inset-0 z-[99] bg-black/50 transition-opacity duration-300 data-closed:opacity-0 data-open:opacity-100"
          aria-hidden="true"
        />
        {/* Panel */}
        <TransitionChild>
          <DialogPanel
            className={cn(asideClassNames, className)}
            style={{ backgroundImage: backgroundGradient }}
          >
            {content}
          </DialogPanel>
        </TransitionChild>
      </Transition>
    </Dialog>
  )
}

export const TopNavV6MobileSlidingPanel = (props: TopNavV6Props) => {
  const { isMobileMenuOpen, setIsMobileMenuOpen, selectedThirdLevelMenu } = useTopNavV6()
  const { isDesktop } = useBreakpoints()

  if (isDesktop) return null

  return (
    <Panel
      open={isMobileMenuOpen}
      onClose={() => setIsMobileMenuOpen(false)}
      className="translate-x-0 data-closed:-translate-x-full"
      content={
        <div className="relative flex h-full flex-col">
          {/* Main Menu Content */}
          <div
            className={cn('flex h-full flex-col transition-opacity duration-300', {
              'opacity-30': !!selectedThirdLevelMenu,
              'opacity-100': !selectedThirdLevelMenu,
            })}
          >
            <TopNavV6MobileCloseHeader onClick={() => setIsMobileMenuOpen(false)} />
            <div className="flex-1 overflow-y-auto">
              <TopNavV6MobileMenu {...props} />
            </div>
          </div>

          {/* Third Level Menu or Mini Menu Overlay */}
          <Transition show={!!selectedThirdLevelMenu} as="div">
            <TransitionChild>
              <div
                className="absolute inset-0 z-10 flex flex-col transition-opacity duration-300 data-closed:opacity-0 data-open:opacity-100"
                style={{ backgroundImage: backgroundGradient }}
              >
                {selectedThirdLevelMenu && (
                  <>
                    {selectedThirdLevelMenu.miniMenu ? (
                      <TopNavV6MobileMiniMenu subNavItem={selectedThirdLevelMenu} />
                    ) : (
                      <TopNavV6MobileThirdLevelMenu subNavItem={selectedThirdLevelMenu} />
                    )}
                  </>
                )}
              </div>
            </TransitionChild>
          </Transition>
        </div>
      }
    />
  )
}
